import React from 'react';
import { Box, Button, Typography } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { useDispatch } from 'react-redux';
import { useAppSelector } from '../../store/hooks';
import { setIsFail, setPhone, setFlowToken } from '../../store/mainSlice';
import { PromotionPackageResponseCodes } from '../../types/promotion';
import FlowLayout from '../FlowLayout';
import { useA11yTokens } from '../../hooks/useA11yTokens';
import { WhatsAppOutlineIcon, WarningTriangleOutlineIcon } from '../../icons';
import { salesWhatsappNumber } from '../../constants';
import { useIsDesktopDevice } from '../../hooks/useIsDesktopDevice';

const ERROR_KEYS: Partial<Record<PromotionPackageResponseCodes, string>> = {
    [PromotionPackageResponseCodes.FormatInvalid]: 'formatInvalid',
    [PromotionPackageResponseCodes.GeneralError]: 'general',
    [PromotionPackageResponseCodes.PackageWasNotFound]: 'packageWasNotFound',
    [PromotionPackageResponseCodes.PackageIsNotActive]: 'packageIsNotActive',
    [PromotionPackageResponseCodes.PackageIsExpired]: 'packageIsExpired',
    [PromotionPackageResponseCodes.ContactWasNotFound]: 'contactWasNotFound',
    [PromotionPackageResponseCodes.ContactIsNotAdmin]: 'contactIsNotAdmin',
    [PromotionPackageResponseCodes.SmsServiceNotAvailiable]: 'smsServiceNotAvailiable',
    [PromotionPackageResponseCodes.SendSmsFailed]: 'sendSmsFailed',
    [PromotionPackageResponseCodes.NoTwoFaCodeWasSetToContact]: 'noTwoFaCodeWasSetToContact',
    [PromotionPackageResponseCodes.TwoFaCodeWasInvalid]: 'twoFaCodeWasInvalid',
    [PromotionPackageResponseCodes.DDOSBlock]: 'ddosBlock',
    [PromotionPackageResponseCodes.FlowTokenNotValid]: 'flowTokenNotValid',
    [PromotionPackageResponseCodes.MaxSmsReached]: 'maxSmsReached',
};

const ERROR_TONES: Record<string, string> = {
    formatInvalid: '#dc2626',
    packageWasNotFound: '#dc2626',
    packageIsNotActive: '#d97706',
    packageIsExpired: '#d97706',
    contactWasNotFound: '#dc2626',
    contactIsNotAdmin: '#dc2626',
    smsServiceNotAvailiable: '#d97706',
    sendSmsFailed: '#d97706',
    noTwoFaCodeWasSetToContact: '#dc2626',
    twoFaCodeWasInvalid: '#dc2626',
    ddosBlock: '#d97706',
    flowTokenNotValid: '#dc2626',
    maxSmsReached: '#dc2626',
    twoFaTooManyTries: '#dc2626',
    general: '#d97706',
};

const Error: React.FC = () => {
    const { t } = useTranslation();
    const theme4dTokens = useA11yTokens();
    const isDesktop = useIsDesktopDevice();
    const navigate = useNavigate();
    const dispatch = useDispatch();
    const { errCode, errSource, packageToken, env } = useAppSelector((state) => state.main);
    const errorKey = 
        errCode === PromotionPackageResponseCodes.MaxSmsReached && errSource === 'verifyCode'
            ? 'twoFaTooManyTries'
            : ERROR_KEYS[errCode as PromotionPackageResponseCodes] ?? 'general';
    const tone = ERROR_TONES[errorKey] ?? ERROR_TONES.general;

    const onTryDifferentNumber = () => {
        dispatch(setIsFail(false));
        dispatch(setPhone(''));
        dispatch(setFlowToken(''));
        navigate(packageToken ? `/?token=${encodeURIComponent(packageToken)}&ENV=${env}` : '/');
    };

    return (
        <FlowLayout>
            <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '12px', mt: '18px', ...(isDesktop && { mt: '50px' }) }}>
                <Box
                    sx={{
                        width: '60px',
                        height: '60px',
                        borderRadius: '999px',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        color: tone,
                        background: `color-mix(in srgb, ${tone} 12%, #fff)`,
                        boxShadow: `0 0 0 9px color-mix(in srgb, ${tone} 6%, transparent)`,
                        mb: '6px',
                    }}
                >
                    <WarningTriangleOutlineIcon sx={{ fontSize: 30 }} />
                </Box>
                <Typography sx={{ fontFamily: theme4dTokens.headFont, fontSize: isDesktop ? 25 : 23, fontWeight: 700, lineHeight: 1.3, color: theme4dTokens.text, textAlign: 'center', m: 0, wordBreak: 'break-word' }}>
                    {t(`errors.${errorKey}.title`)}
                </Typography>
                <Typography sx={{ fontSize: isDesktop ? 17 : 15, lineHeight: 1.65, color: theme4dTokens.muted, textAlign: 'center', m: 0, wordBreak: 'break-word', ...(isDesktop && { mt: '16px' }) }}>
                    {t(`errors.${errorKey}.msg`)}
                </Typography>
                {(errorKey === 'contactWasNotFound' || errorKey === 'flowTokenNotValid') && (
                    <Button
                        variant="contained"
                        fullWidth
                        disableElevation
                        onClick={onTryDifferentNumber}
                        sx={{
                            fontFamily: theme4dTokens.headFont,
                            bgcolor: theme4dTokens.accent,
                            color: theme4dTokens.accentText,
                            borderRadius: theme4dTokens.radius,
                            py: '13px',
                            fontSize: isDesktop ? 17 : 15,
                            fontWeight: 700,
                            mt: '4px',
                            wordBreak: 'break-word',
                            '&:hover': { bgcolor: theme4dTokens.accent, filter: 'brightness(1.07)' },
                        }}
                    >
                        {t(`errors.${errorKey}.cta`)}
                    </Button>
                )}
            </Box>

            <Box sx={{ bgcolor: theme4dTokens.surface, border: `1px solid ${theme4dTokens.border}`, borderRadius: theme4dTokens.radius, p: '16px', display: 'flex', flexDirection: 'column', gap: '12px', mt: 'auto' }}>
                <Typography sx={{ fontFamily: theme4dTokens.headFont, fontSize: isDesktop ? 15.5 : 13.5, fontWeight: 700, color: theme4dTokens.muted, textAlign: 'center', m: 0, wordBreak: 'break-word' }}>
                    {t('errors.supportTitle')}
                </Typography>
                <Box sx={{ display: 'grid', gridTemplateColumns: '1fr', gap: '10px' }}>
                    {/* <Box
                        component="a"
                        href="tel:031234567"
                        sx={{
                            display: 'flex',
                            flexDirection: 'column',
                            alignItems: 'center',
                            gap: '3px',
                            p: '13px 10px',
                            bgcolor: theme4dTokens.bg,
                            border: `1px solid ${theme4dTokens.border}`,
                            borderRadius: theme4dTokens.radius,
                            textDecoration: 'none',
                            color: theme4dTokens.text,
                            '&:hover': { borderColor: theme4dTokens.accent },
                        }}
                    >
                        <Box
                            sx={{
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                width: 34,
                                height: 34,
                                borderRadius: '999px',
                                bgcolor: theme4dTokens.accentSoft,
                                color: theme4dTokens.accent,
                                mb: '3px',
                            }}
                        >
                            <PhoneOutlineIcon sx={{ fontSize: 18 }} />
                        </Box>
                        <Typography sx={{ fontFamily: theme4dTokens.headFont, fontSize: 14, fontWeight: 700, textAlign: 'center', wordBreak: 'break-word' }}>
                            {t('errors.callSupportLabel')}
                        </Typography>
                        <Typography sx={{ fontSize: 12, color: theme4dTokens.muted, direction: 'ltr', wordBreak: 'break-word', textAlign: 'center' }}>03-123-4567</Typography>
                    </Box> */}
                    <Box
                        component="a"
                        href={`https://wa.me/${salesWhatsappNumber}`}
                        target="_blank"
                        rel="noopener"
                        sx={{
                            display: 'flex',
                            flexDirection: 'column',
                            alignItems: 'center',
                            gap: '3px',
                            p: '13px 10px',
                            bgcolor: theme4dTokens.bg,
                            border: `1px solid ${theme4dTokens.border}`,
                            borderRadius: theme4dTokens.radius,
                            textDecoration: 'none',
                            color: theme4dTokens.text,
                            '&:hover': { borderColor: '#25D366' },
                        }}
                    >
                        <Box
                            sx={{
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                width: 34,
                                height: 34,
                                borderRadius: '999px',
                                bgcolor: 'rgba(37,211,102,.14)',
                                color: '#1aa04e',
                                mb: '3px',
                            }}
                        >
                            <WhatsAppOutlineIcon sx={{ fontSize: 18 }} />
                        </Box>
                        <Typography sx={{ fontFamily: theme4dTokens.headFont, fontSize: isDesktop ? 16 : 14, fontWeight: 700, textAlign: 'center', wordBreak: 'break-word' }}>
                            WhatsApp
                        </Typography>
                        <Typography sx={{ fontSize: isDesktop ? 14 : 12, color: theme4dTokens.muted, textAlign: 'center', wordBreak: 'break-word' }}>
                            {t('errors.whatsappSub')}
                        </Typography>
                    </Box>
                </Box>
            </Box>

            {/* <Typography sx={{ fontSize: 12.5, color: theme4dTokens.muted, textAlign: 'center', lineHeight: 1.6, m: 0 }}>
                {t('offer.contactSalesPrefix')}{' '}
                <Box
                    component="a"
                    href={`https://wa.me/${salesWhatsappNumber}`}
                    target="_blank"
                    rel="noopener"
                    sx={{ color: theme4dTokens.accent, fontWeight: 700, textDecoration: 'none', wordBreak: 'break-word' }}
                >
                    {t('offer.salesTeam')}
                </Box>
            </Typography> */}
        </FlowLayout>
    );
};

export default Error;
